import React, { useEffect, useMemo, useState } from 'react';
import AdminStatCard from './AdminStatCard.jsx';
import AdminSectionTitle from './AdminSectionTitle.jsx';
import AdminTrendChart from './AdminTrendChart.jsx';
import AdminRecentTable from './AdminRecentTable.jsx';
import { AdminCard, AdminPageShell, AdminSelect } from '../AdminUI.jsx';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const rangeOptions = [
  { label: 'Last 30 Days', value: '30d' },
  { label: 'Last 90 Days', value: '90d' },
  { label: 'Last 365 Days', value: '365d' },
  { label: 'All Time', value: 'all' }
];

const formatCurrency = (value) => {
  return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const AdminDashboardPage = () => {
  const token = localStorage.getItem('adminToken');
  const [range, setRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);

  const fetchDashboard = async (nextRange = range) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/admin/dashboard?range=${encodeURIComponent(nextRange)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load dashboard');
      setDashboard(data);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const mergedFinancialTrend = useMemo(() => {
    const payments = dashboard?.trends?.payments || [];
    const commissions = dashboard?.trends?.commissions || [];
    const byDate = new Map();

    payments.forEach((item) => {
      byDate.set(item.date, {
        date: item.date,
        income: Number(item.income_cents || 0) / 100,
        commissions: 0,
        revenue: Number(item.income_cents || 0) / 100
      });
    });

    commissions.forEach((item) => {
      const existing = byDate.get(item.date) || { date: item.date, income: 0, commissions: 0, revenue: 0 };
      existing.commissions = Number(item.commissions || 0);
      existing.revenue = existing.income - existing.commissions;
      byDate.set(item.date, existing);
    });

    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [dashboard]);

  const summary = dashboard?.summaryCards || {};
  const financial = summary.financial || {};

  return (
    <AdminPageShell
      title="Admin Dashboard"
      description="Executive overview of users, affiliates, and financial performance."
      actions={(
        <div className="min-w-48">
          <AdminSelect
            id="dashboard-range"
            value={range}
            onChange={(e) => {
              const next = e.target.value;
              setRange(next);
              fetchDashboard(next);
            }}
            className="border-white/50 bg-white/20 text-white focus:border-white focus:ring-white/25"
          >
            {rangeOptions.map((option) => (
              <option key={option.value} value={option.value} className="text-slate-900">
                {option.label}
              </option>
            ))}
          </AdminSelect>
        </div>
      )}
    >
      {error ? <AdminCard className="border-rose-200 text-rose-600 dark:border-rose-900/40">{error}</AdminCard> : null}
      {loading ? <AdminCard>Loading dashboard...</AdminCard> : null}

      {!loading && dashboard ? (
        <>
          <AdminSectionTitle title="Financial KPIs" description="Revenue = Total Income - Total Commissions" />
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <AdminStatCard title="Total Income" value={formatCurrency(financial.totalIncome)} />
            <AdminStatCard title="Total Commissions" value={formatCurrency(financial.totalCommissions)} />
            <AdminStatCard title="Revenue" value={formatCurrency(financial.revenue)} />
          </div>

          <AdminSectionTitle title="Users & Affiliates" />
          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <AdminRecentTable
              columns={[
                { key: 'status', label: 'User Status' },
                { key: 'count', label: 'Count' }
              ]}
              rows={dashboard.statusBreakdowns?.users || []}
              emptyMessage="No user status data."
            />
            <AdminRecentTable
              columns={[
                { key: 'status', label: 'Affiliate Status' },
                { key: 'count', label: 'Count' }
              ]}
              rows={dashboard.statusBreakdowns?.affiliates || []}
              emptyMessage="No affiliate status data."
            />
          </div>

          <AdminSectionTitle title="Trend Charts" />
          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <p className="mb-2 text-sm font-semibold text-slate-600 dark:text-slate-300">Financial Momentum (Income, Revenue, Commissions)</p>
              <AdminTrendChart data={mergedFinancialTrend} type="financial-combo" />
            </div>
            <div>
              <p className="mb-2 text-sm font-semibold text-slate-600 dark:text-slate-300">Revenue Area Trend</p>
              <AdminTrendChart data={mergedFinancialTrend} dataKey="revenue" stroke="#3b82f6" type="area" />
            </div>
            <div>
              <p className="mb-2 text-sm font-semibold text-slate-600 dark:text-slate-300">Income Trend</p>
              <AdminTrendChart data={mergedFinancialTrend} dataKey="income" stroke="#10b981" type="line" />
            </div>
            <div>
              <p className="mb-2 text-sm font-semibold text-slate-600 dark:text-slate-300">New Users Trend</p>
              <AdminTrendChart data={dashboard.trends?.users || []} dataKey="count" stroke="#8b5cf6" type="area" />
            </div>
          </div>

          <AdminSectionTitle title="Promo & Campaign Performance" />
          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <AdminRecentTable
              columns={[
                { key: 'code', label: 'Promo Code' },
                { key: 'usages_count', label: 'Usages' },
                { key: 'discounts_cents', label: 'Discount Total', render: (row) => formatCurrency(Number(row.discounts_cents || 0) / 100) }
              ]}
              rows={dashboard.performance?.topPromoCodes || []}
              emptyMessage="No promo performance data."
            />
            <AdminRecentTable
              columns={[
                { key: 'name', label: 'Campaign' },
                { key: 'payments_count', label: 'Payments' },
                { key: 'savings_cents', label: 'Savings Total', render: (row) => formatCurrency(Number(row.savings_cents || 0) / 100) }
              ]}
              rows={dashboard.performance?.topCampaigns || []}
              emptyMessage="No campaign performance data."
            />
          </div>
        </>
      ) : null}
    </AdminPageShell>
  );
};

export default AdminDashboardPage;
